import { Link, useParams } from 'react-router-dom';
import { ChevronRight, FileText, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/SEO/SEOHead';

export function CGVPage() {
  const { locale } = useParams<{ locale: string }>();
  const { t, i18n } = useTranslation('cgv');
  const currentLocale = locale || i18n.language || 'fr';

  const articles = t('articles', { returnObjects: true }) as any[];
  const alert = t('alert', { returnObjects: true }) as any;
  const companyInfo = t('companyInfo', { returnObjects: true }) as any;
  const footer = t('footer', { returnObjects: true }) as any;
  const cta = t('cta', { returnObjects: true }) as any;

  return (
    <div>
      <SEOHead
        title={t('title')}
        description={t('subtitle')}
        path="/cgv"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-aspexit-gray">
            <Link to={`/${currentLocale}`} className="hover:text-aspexit-green">
              {t('breadcrumb.home')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-aspexit-black">
              {t('breadcrumb.current')}
            </span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <FileText className="w-10 h-10 text-aspexit-green" />
              <h1 className="text-4xl font-heading font-bold text-aspexit-black">
                {t('title')}
              </h1>
            </div>
            <p className="text-aspexit-gray">
              {t('subtitle')}
            </p>
          </div>

          {/* Alert notice */}
          <div className="bg-blue-50 border-l-4 border-aspexit-purple rounded-lg p-6 mb-12">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-aspexit-purple flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-aspexit-black mb-2">
                  {alert.title}
                </h3>
                <p className="text-sm text-aspexit-gray">
                  {alert.content}
                </p>
              </div>
            </div>
          </div>

          {/* CGV Content */}
          <div className="prose prose-lg max-w-none space-y-12">
            {articles.map((article: any) => (
              <section key={article.number}>
                <h2 className="text-2xl font-heading font-extrabold text-aspexit-black mb-6">
                  Article {article.number} - {article.title}
                </h2>

                {/* Article 1 special: company info */}
                {article.number === "1" && article.intro && (
                  <>
                    <p className="text-aspexit-gray mb-4">{article.intro}</p>
                    <div className="bg-aspexit-gray-light rounded-lg p-4 mb-4">
                      <p className="text-aspexit-gray mb-1"><strong>{companyInfo.name}</strong></p>
                      <p className="text-aspexit-gray text-sm">{companyInfo.legalForm}</p>
                      <p className="text-aspexit-gray text-sm">SIREN : {companyInfo.siren}</p>
                      <p className="text-aspexit-gray text-sm">{companyInfo.address}</p>
                      <p className="text-aspexit-gray text-sm">{companyInfo.representative}</p>
                    </div>
                  </>
                )}

                {/* Intro text */}
                {article.intro && article.number !== "1" && (
                  <p className="text-aspexit-gray mb-4">{article.intro}</p>
                )}

                {/* Simple list */}
                {article.list && !article.subsections && (
                  <ul className="list-disc pl-6 text-aspexit-gray space-y-2">
                    {article.list.map((item: string, idx: number) => {
                      const parts = item.split(':');
                      if (parts.length > 1) {
                        return <li key={idx}><strong>{parts[0]}:</strong>{parts.slice(1).join(':')}</li>;
                      }
                      return <li key={idx}>{item}</li>;
                    })}
                  </ul>
                )}

                {/* Content paragraphs */}
                {article.content && article.content.map((para: string, idx: number) => (
                  <p key={idx} className="text-aspexit-gray mb-4">{para}</p>
                ))}

                {/* Subsections */}
                {article.subsections && article.subsections.map((subsection: any, idx: number) => (
                  <div key={idx} className="mt-6">
                    <h3 className="text-xl font-semibold text-aspexit-black mb-3">
                      {subsection.title}
                    </h3>

                    {subsection.intro && (
                      <p className="text-aspexit-gray mb-4">{subsection.intro}</p>
                    )}

                    {subsection.list && (
                      <ul className="list-disc pl-6 text-aspexit-gray space-y-2 mb-4">
                        {subsection.list.map((item: string, i: number) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    )}

                    {subsection.content && subsection.content.map((para: string, i: number) => (
                      <p key={i} className="text-aspexit-gray mb-4">{para}</p>
                    ))}

                    {/* Groups for article 5 (cancellation policies) */}
                    {subsection.groups && subsection.groups.map((group: any, i: number) => (
                      <div key={i} className="bg-aspexit-gray-light rounded-lg p-6 mb-4">
                        <p className="text-aspexit-gray mb-3"><strong>{group.subtitle}</strong></p>
                        <ul className="list-disc pl-6 text-aspexit-gray space-y-2">
                          {group.list.map((item: string, j: number) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Link for article 13 (Privacy Policy) */}
                {article.linkText && (
                  <p className="text-aspexit-gray">
                    {article.linkText}{' '}
                    <Link
                      to={`/${currentLocale}${article.linkUrl}`}
                      className="text-aspexit-green hover:underline"
                    >
                      {article.linkLabel}
                    </Link>.
                  </p>
                )}
              </section>
            ))}

            {/* Footer info */}
            <section>
              <div className="bg-aspexit-gray-light rounded-lg p-6 text-center">
                <p className="text-sm text-aspexit-gray mb-2">
                  <strong>{footer.versionLabel}</strong> {t('lastUpdate')}
                </p>
                <p className="text-sm text-aspexit-gray">
                  {footer.modificationText}
                </p>
              </div>
            </section>
          </div>

          {/* CTA Contact */}
          <div className="mt-12 text-center bg-aspexit-green/10 rounded-lg p-8">
            <h3 className="text-xl font-heading font-extrabold text-aspexit-black mb-6">
              {cta.title}
            </h3>
            <p className="text-aspexit-gray mb-6">
              {cta.subtitle}
            </p>
            <Link
              to={`/${currentLocale}/contact`}
              className="inline-block px-6 py-3 bg-aspexit-green text-white rounded-lg font-semibold hover:bg-aspexit-green-dark transition-colors"
            >
              {cta.buttonText}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
